/**
 * What a build will cost, said before the button rather than after it.
 *
 * Walked the web flow on a phone on 2026-09-25: six steps of choices, "Build this app", and an
 * anonymous visitor lands on a Create account screen — having never been told the price, and
 * never having been told an account was needed at all. The native app does better and still
 * not well: "This build uses 1,000 credits" appears only on the final step.
 *
 * One function, used by both clients, because the price a person is told is exactly the kind
 * of thing that drifts when each client writes its own sentence — and the two clients here
 * already share no publish code, which is how everyone's apps once ended up in Discover.
 *
 * It answers with a TONE as well as words, so a client can style "you need a plan" differently
 * from "you are short" without parsing the sentence to find out which it got.
 *
 * Since 2026-09-29 there is no free tier (the owner's decision): no free credits for a new
 * account and no free first app, for anybody — a seven-day free trial of the subscription
 * instead. Accounts made before keep whatever credits they have, so a balance that covers the
 * build is still simply priced. This used to say "Your first app is free" and "This one is
 * free"; both would now be said to people who then meet a paywall at the Build button.
 */

/*
 * Written out rather than imported, and pinned to the server by the test instead.
 *
 * The true source is `OPERATION_CREDITS` in infra/lambda/shared/credits.ts, and importing it
 * would be the obvious single source. But credits.ts imports models.ts — which reads
 * `process.env` at load and carries every model id and note the server uses — so one import
 * here would ship the server's model table into both client bundles, and this file is on
 * the web's boundary allowlist precisely because it imports nothing. The test compares these
 * to the server's table itself, so a server price change turns it red.
 */
/** The server's `OPERATION_CREDITS.generation`. A price shown must be the price charged. */
export const BUILD_COST = 1000;
/** The server's `OPERATION_CREDITS.edit`. */
export const EDIT_COST = 500;
/**
 * What tailoring a plan costs: `/plan` is charged in the `edit` category (webBuild.ts). So a
 * NEW app is this plus BUILD_COST — the whole journey, which is the only view a person has.
 */
export const PLAN_COST = EDIT_COST;
/** The server's `PAID_GRANT_CREDITS`: what a subscription, trial included, grants each month. */
export const MONTHLY_CREDITS = 20000;
/** A new app: its plan and its build. */
const NEW_APP_COST = PLAN_COST + BUILD_COST;

export type CostTone =
  /** Signed out: the account is the first ask, and the subscription the second. */
  | 'invite'
  /** Signed in with no plan, and the balance will not cover it: the subscription is the way. */
  | 'subscribe'
  /** It costs, and they can afford it. */
  | 'cost'
  /** A subscriber whose balance will not cover it — said while they can still do something. */
  | 'short'
  /** Nothing honest to say: unlimited, or the server has not answered yet. */
  | 'silent';

export interface CostNote {
  tone: CostTone;
  headline: string;
  detail: string;
}

export interface CostInput {
  signedIn: boolean;
  /** 'free' is an account with no plan — the name of a tier that no longer gives anything. */
  tier?: 'free' | 'paid' | 'unlimited';
  creditsRemaining?: number;
  /**
   * Not read since 2026-09-29: it only ever decided "this one is free", and nothing is. Kept so
   * callers that still pass it compile.
   */
  firstBuildDone?: boolean;
  /**
   * The caller can keep this screen's choices across a top-up or a checkout. Absent means NO.
   *
   * True on the phone, whose paywall is a screen pushed over the wizard and whose draft is
   * still there when it closes. False in the browser until its draft survives the trip: checkout
   * is a full-page navigation to Stripe, and the person came back to an empty /new having been
   * told "everything you have chosen here is kept". A client has to claim this, so a new caller
   * that forgets makes no promise instead of a false one.
   */
  topUpKeepsChoices?: boolean;
  /**
   * The free trial, in days, that the caller can PROMISE to somebody with no plan. Absent means
   * none is mentioned.
   *
   * Claimed for the same reason as `topUpKeepsChoices`: only the caller knows. In a browser the
   * server opens every subscription checkout with a seven-day trial (the shared contract of
   * 2026-09-29), so the web can say 7. On the phone the trial is Apple's introductory offer, which
   * Apple gives once per Apple ID; a caller that has not asked StoreKit must not say it.
   */
  trialDays?: number;
}

const n = (v: number) => v.toLocaleString();
/** "up to 1,500 credits (500 to plan, 1,000 to build)" — quoted where the plan leg is still ahead. */
const newAppPrice = () => `up to ${n(NEW_APP_COST)} credits (${n(PLAN_COST)} to plan, ${n(BUILD_COST)} to build)`;
const SILENT: CostNote = { tone: 'silent', headline: '', detail: '' };

/** A trial length somebody can be promised, or null when the caller claimed none (or nonsense). */
const trialOf = (days: number | undefined): number | null =>
  typeof days === 'number' && Number.isInteger(days) && days > 0 ? days : null;

export function buildCostNote(input: CostInput): CostNote {
  const trial = trialOf(input.trialDays);
  const pro = `Saddle Pro for ${n(MONTHLY_CREDITS)} credits a month`;

  /*
   * Signed out first, because this is the case both clients got wrong and the only one where the
   * first answer is not about money at all. Saying it HERE, next to the button, is the minimum;
   * saying it before six steps of work would be better still.
   */
  if (!input.signedIn) {
    const then = trial ? `start a ${trial}-day free trial of ${pro}` : `subscribe to ${pro}`;
    return {
      tone: 'invite',
      headline: 'Building needs an account and Saddle Pro',
      detail: `You'll make an account, then ${then}. A new app uses ${newAppPrice()}, and a change ${n(EDIT_COST)}.`,
    };
  }

  // Nothing to bill, so nothing to say. A price note on an unlimited account is noise.
  if (input.tier === 'unlimited') return SILENT;

  // Quiet rather than guessing: a price we are not sure of is worse than none — somebody acts on it.
  if (input.creditsRemaining === undefined) return SILENT;

  if (input.creditsRemaining >= BUILD_COST) {
    return {
      tone: 'cost',
      headline: `This build uses ${n(BUILD_COST)} credits`,
      detail: `You have ${n(input.creditsRemaining)}. Changing it afterwards costs ${n(EDIT_COST)} a time.`,
    };
  }

  /*
   * Short, with no plan: the way past is the subscription, never a "top up" — the top-up is sold
   * to subscribers only. What is left is said only when there is some (an older account's
   * credits, kept); "You have 0" said to somebody who never had any reads as a scolding.
   */
  if (input.tier === 'free') {
    const have = input.creditsRemaining > 0 ? `You have ${n(input.creditsRemaining)}. ` : '';
    const start = trial ? `Start a ${trial}-day free trial of ${pro}` : `Subscribe to ${pro}`;
    const kept = input.topUpKeepsChoices ? ' — everything you have chosen here is kept' : '';
    return {
      tone: 'subscribe',
      headline: `This build needs ${n(BUILD_COST)} credits`,
      detail: `${have}${start}${kept}.`,
    };
  }

  if (input.tier === 'paid') {
    return {
      tone: 'short',
      headline: `This build needs ${n(BUILD_COST)} credits`,
      detail: input.topUpKeepsChoices
        ? `You have ${n(input.creditsRemaining)}. Top up and everything you have chosen here is kept.`
        : `You have ${n(input.creditsRemaining)}. Top up to build it.`,
    };
  }

  // Short, and nobody has said whether there is a plan: "subscribe" or "top up" would be a guess.
  return SILENT;
}
